import React from 'react';
import { LocationOn, Schedule } from "@mui/icons-material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import Container from "@mui/material/Container"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import CircularProgress from "@mui/material/CircularProgress"
import Alert from "@mui/material/Alert"
import { useContactForm } from "../../../../hooks/useContactForm"

const ContactUs: React.FC = () => {
  const {
    formData,
    formErrors,
    isSending,
    isSuccess,
    error,
    handleInputChange,
    handleSubmit,
  } = useContactForm()

  return (
    <Box sx={{ backgroundColor: 'white', py: 8 }}>
      <Container maxWidth="lg">

        <Box id="contact-us-section" sx={{ textAlign: 'center', mb: { xs: 4, sm: 5, md: 6 }, px: { xs: 2, sm: 0 } }}>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{
              color: 'primary.dark',
              fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.75rem', lg: '1.875rem' },
              mb: { xs: 1.5, md: 2 }
            }}
          >
            ¿Quieres más información?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.938rem', sm: '1rem', md: '1.063rem' },
              lineHeight: { xs: 1.6, md: 1.7 }
            }}
          >
            Póngase en contacto con nosotros para admisiones, consultas o cualquier otra información sobre la Institución Educativa José Eusebio Caro.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 3,
            p: 1,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
          >
            {/* Contact Info */}
            <Box sx={{ flex: 1 }}>
              <Stack spacing={3} sx={{ mx: { xs: 0, md: 2, lg: 6, xl: 10 } }}>
                {/* Dirección */}
                <Card sx={{ p: 3, borderRadius: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <LocationOn sx={{ color: 'primary.dark' }} />
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem' } }}
                      >
                        Dirección
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Calle 5 # 36-00, Popayán, Cauca
                      </Typography>
                    </Box>
                  </Stack>
                </Card>

                {/* Horarios */}
                <Card sx={{ p: 3, borderRadius: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Schedule sx={{ color: 'primary.dark' }} />
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem' } }}
                      >
                        Horarios
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lunes a Viernes: 7:00 am - 3:00 pm
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Stack>
            </Box>

            {/* Contact Form */}
            <Box sx={{ flex: 1, mt: { xs: 4, md: 0 } }}>
              <Card sx={{ p: 4, borderRadius: 2 }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                      Nombre
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Ingrese Nombre Completo"
                      variant="filled"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      disabled={isSending}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          height: '40px',
                          backgroundColor: 'primary.50',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          paddingTop: '0 !important',
                          '&:hover': {
                            backgroundColor: 'primary.50',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'primary.50',
                          },
                          '& input': {
                            padding: '0 12px',
                          },
                        },
                      }}
                      sx={{
                        '& .MuiFilledInput-root': {
                          backgroundColor: 'primary.50',
                          '&:hover': {
                            backgroundColor: 'primary.50',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'primary.50',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                        Email
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Ingrese Email de contacto"
                        variant="filled"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        disabled={isSending}
                        type="email"
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            height: '40px',
                            backgroundColor: 'primary.50',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: '0 !important',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'primary.50',
                            },
                            '& input': {
                              padding: '0 12px',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiFilledInput-root': {
                            backgroundColor: 'primary.50',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'primary.50',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                        Celular
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Ingrese Celular de contacto"
                        variant="filled"
                        value={formData.phone}
                        onChange={handleInputChange('phone')}
                        error={!!formErrors.phone}
                        helperText={formErrors.phone}
                        type="tel"
                        disabled={isSending}
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            height: '40px',
                            backgroundColor: 'primary.50',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: '0 !important',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'primary.50',
                            },
                            '& input': {
                              padding: '0 12px',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiFilledInput-root': {
                            backgroundColor: 'primary.50',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'primary.50',
                            },
                          },
                        }}
                      />
                    </Box>
                  </Stack>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                      Asunto
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Ingrese Asunto del Mensaje"
                      variant="filled"
                      value={formData.subject}
                      onChange={handleInputChange('subject')}
                      error={!!formErrors.subject}
                      helperText={formErrors.subject}
                      disabled={isSending}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          height: '40px',
                          backgroundColor: 'primary.50',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          paddingTop: '0 !important',
                          '&:hover': {
                            backgroundColor: 'primary.50',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'primary.50',
                          },
                          '& input': {
                            padding: '0 12px',
                          },
                        },
                      }}
                      sx={{
                        '& .MuiFilledInput-root': {
                          backgroundColor: 'primary.50',
                          '&:hover': {
                            backgroundColor: 'primary.50',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'primary.50',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                      Mensaje
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Ingrese Mensaje"
                      multiline
                      rows={6}
                      variant="filled"
                      value={formData.message}
                      onChange={handleInputChange('message')}
                      error={!!formErrors.message}
                      helperText={formErrors.message}
                      disabled={isSending}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          backgroundColor: 'primary.50',
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'primary.50',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'primary.50',
                          },
                        },
                      }}
                      sx={{
                        '& .MuiFilledInput-root': {
                          backgroundColor: 'primary.50',
                          '&:hover': {
                            backgroundColor: 'primary.50',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'primary.50',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={isSending}
                    sx={{
                      backgroundColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      color: 'white',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    {isSending ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Enviando...
                      </>
                    ) : (
                      'Enviar Mensaje'
                    )}
                  </Button>
                </Stack>
              </Card>
              {/* Success/Error Messages */}
              {isSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  ¡Mensaje enviado exitosamente! Nos pondremos en contacto pronto.
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  Error al enviar el mensaje: {error}
                </Alert>
              )}
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default ContactUs